import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';
import { LEADERSHIP } from '../data/content';
import SEO from '../components/SEO';

const Leadership = () => {
  return (
    <div className="bg-[#050505] text-white" data-testid="leadership-page">
      <SEO
        title="Leadership — Bronto Intelligence Board and Executive Team"
        description="Meet the leadership of Bronto Intelligence Private Limited, including Founder Jeet Santosh Bhaskar and Non-Executive Director Santosh Bhaskar."
        path="/leadership"
      />
      {/* Hero */}
      <section className="relative pt-20 pb-24 px-6 lg:px-10 overflow-hidden">
        <div className="aurora-bg" />
        <div className="relative z-10 max-w-7xl mx-auto">
          <div className="text-xs font-semibold uppercase tracking-[0.25em] text-white/40 mb-6">
            Leadership
          </div>
          <h1 className="text-display text-5xl md:text-7xl lg:text-8xl mb-10 max-w-5xl">
            Board of <span className="rainbow-text">Directors.</span>
          </h1>
          <p className="text-xl md:text-2xl text-white/65 max-w-3xl leading-relaxed font-light">
            The Board of Directors of Bronto Intelligence Private Limited brings together diverse perspectives across technology and research to support the company&apos;s long-term vision and direction.
          </p>
        </div>
      </section>

      {/* Directors */}
      <section className="py-16 px-6 lg:px-10 border-t border-white/5">
        <div className="max-w-6xl mx-auto space-y-12">
          {LEADERSHIP.map((leader, idx) => (
            <Link
              key={leader.id}
              to={`/leadership/${leader.id}`}
              className="group block rounded-3xl glass hover-lift overflow-hidden"
              data-testid={`director-card-${leader.id}`}
            >
              <div className="grid md:grid-cols-12 gap-0">
                {/* Photo */}
                <div className="md:col-span-4 relative aspect-square md:aspect-auto md:min-h-[420px] bg-gradient-to-br from-white/[0.06] to-white/[0.02] overflow-hidden">
                  {leader.photoUrl ? (
                    <img
                      src={leader.photoUrl}
                      alt={leader.name}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <div className="text-7xl font-bold text-white/15">
                        {leader.name.split(' ').map(n => n[0]).join('').slice(0, 2)}
                      </div>
                    </div>
                  )}
                </div>

                {/* Info */}
                <div className="md:col-span-8 p-10 md:p-14 flex flex-col justify-center">
                  <div className="text-xs font-semibold uppercase tracking-[0.25em] text-white/40 mb-4">
                    0{idx + 1} · {leader.title}
                  </div>
                  <h2 className="text-4xl md:text-5xl font-bold tracking-tight mb-6">
                    {leader.name}
                  </h2>
                  <p className="text-base md:text-lg text-white/65 leading-relaxed mb-8 max-w-2xl">
                    {leader.shortBio}
                  </p>

                  {/* Responsibilities */}
                  {leader.responsibilities?.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-8">
                      {leader.responsibilities.map((resp) => (
                        <span
                          key={resp}
                          className="text-xs font-medium text-white/70 px-3 py-1.5 rounded-full bg-white/5 border border-white/10"
                        >
                          {resp}
                        </span>
                      ))}
                    </div>
                  )}

                  <div className="inline-flex items-center gap-2 text-sm font-medium text-white border-b border-white/30 pb-1 self-start group-hover:border-white transition-colors">
                    View full biography
                    <ArrowUpRight className="h-3.5 w-3.5" />
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Governance Note */}
      <section className="py-24 px-6 lg:px-10 border-t border-white/5">
        <div className="max-w-4xl mx-auto text-center">
          <div className="text-xs font-semibold uppercase tracking-[0.25em] text-white/40 mb-6">
            Governance
          </div>
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-6">
            Committed to sound corporate governance.
          </h2>
          <p className="text-base md:text-lg text-white/60 leading-relaxed">
            The Board of Directors provides oversight, strategic guidance, and governance for Bronto Intelligence Private Limited. The board ensures that the company operates with integrity, transparency, and a long-term orientation across its product, platform, and technology initiatives.
          </p>
        </div>
      </section>
    </div>
  );
};

export default Leadership;
